import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

export default function ChangePasswordDialog({ open, onOpenChange }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [busy, setBusy] = useState(false);

  React.useEffect(() => {
    if (open) {
      setCurrent("");
      setNext("");
      setConfirmPw("");
    }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirmPw) {
      toast.error("New password and confirmation do not match");
      return;
    }
    if (next.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      toast.success("Password changed successfully");
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none border vsm-border max-w-md" data-testid="change-password-dialog">
        <DialogHeader>
          <div className="vsm-overline">Account</div>
          <DialogTitle className="text-2xl font-bold tracking-tight">Change Password</DialogTitle>
          <DialogDescription className="vsm-muted text-sm">
            New password must be at least 6 characters and different from your current one.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div>
            <Label className="vsm-overline">Current Password <span className="text-red-600">*</span></Label>
            <Input
              data-testid="cp-current"
              type="password"
              className="rounded-none mt-2"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              required
              autoFocus
            />
          </div>
          <div>
            <Label className="vsm-overline">New Password <span className="text-red-600">*</span></Label>
            <Input
              data-testid="cp-new"
              type="password"
              className="rounded-none mt-2"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              required
              minLength={6}
            />
          </div>
          <div>
            <Label className="vsm-overline">Confirm New Password <span className="text-red-600">*</span></Label>
            <Input
              data-testid="cp-confirm"
              type="password"
              className="rounded-none mt-2"
              value={confirmPw}
              onChange={(e) => setConfirmPw(e.target.value)}
              required
              minLength={6}
            />
          </div>
          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="outline"
              className="rounded-none"
              onClick={() => onOpenChange(false)}
              data-testid="cp-cancel"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={busy}
              className="rounded-none vsm-primary-bg hover:opacity-90"
              data-testid="cp-save"
            >
              {busy ? "Updating…" : "Update Password"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
